import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import { AxiosError } from "axios";
import { CreateDropPeriodDto, DropPeriodApi, UpdateDropPeriodDto, DropPeriod } from "../../api";
import { serializeError } from "../../app/helpers";
import { RootState } from "../../app/store";
import { ApiError } from "../../app/types";

export interface DropPeriodState {
  dropPeriod: DropPeriod[];
  status: "idle" | "loading" | "failed";
  error?: ApiError;
}

const initialState: DropPeriodState = {
  dropPeriod: [],
  status: "idle",
};

export const createDropPeriodAsync = createAsyncThunk(
  "dropPeriod/create",
  async (dropPeriod: CreateDropPeriodDto, { rejectWithValue }) => {
    try {
      const dropPeriodApi = new DropPeriodApi();
      const response = await dropPeriodApi.dropPeriodControllerCreate(dropPeriod);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateDropPeriodAsync = createAsyncThunk(
  "dropPeriod/update",
  async ({ dropPeriod, id }: { dropPeriod: UpdateDropPeriodDto; id: string }, { rejectWithValue }) => {
    try {
      const dropPeriodApi = new DropPeriodApi();
      const response = await dropPeriodApi.dropPeriodControllerUpdate(dropPeriod, id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteDropPeriodAsync = createAsyncThunk(
  "dropPeriod/remove",
  async ({ dropPeriod }: { dropPeriod: DropPeriod }, { rejectWithValue }) => {
    try {
      const dropPeriodApi = new DropPeriodApi();
      const response = await dropPeriodApi.dropPeriodControllerRemove(dropPeriod.id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchDropPeriodByOrganizationIdAsync = createAsyncThunk("dropPeriod/fetch", async ({ organizationId, year, quarter }: { organizationId: string, year: number, quarter: number }, { rejectWithValue }) => {
  try {
    const dropPeriodApi = new DropPeriodApi();
    const response = await dropPeriodApi.dropPeriodControllerFindByOrganizationIdYearQuarter(organizationId, year, quarter);
    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const createExtraReducers = () => {
  const fetchDropPeriodByOrganization = () => {
    const { pending, fulfilled, rejected } = fetchDropPeriodByOrganizationIdAsync;
    return {
      [`${pending}`]: (state: DropPeriodState) => {
        state.status = "loading";
      },
      [`${fulfilled}`]: (state: DropPeriodState, action: PayloadAction<DropPeriod[]>) => {
        state.dropPeriod = action.payload;
        state.status = "idle";
      },
      [`${rejected}`]: (state: DropPeriodState, action: PayloadAction<AxiosError>) => {
        const error = serializeError(action.payload);
        state.status = "failed";
        state.error = error;
      },
    };
  };

  const createDropPeriod = () => {
    const { pending, fulfilled, rejected } = createDropPeriodAsync;
    return {
      [`${pending}`]: (state: DropPeriodState) => {
        state.status = "loading";
      },
      [`${fulfilled}`]: (state: DropPeriodState, action: PayloadAction<DropPeriod>) => {
        state.dropPeriod.push(action.payload);
        message.success("Успешно создан");
        state.status = "idle";
      },
      [`${rejected}`]: (state: DropPeriodState, action: PayloadAction<AxiosError>) => {
        const error = serializeError(action.payload);
        state.status = "failed";
        message.error("Произошла ошибка !");
        state.error = error;
      },
    };
  };

  const updateDropPeriod = () => {
    const { pending, fulfilled, rejected } = updateDropPeriodAsync;

    return {
      [`${pending}`]: (state: DropPeriodState) => {
        state.status = "loading";
      },
      [`${fulfilled}`]: (state: DropPeriodState, action: PayloadAction<DropPeriod>) => {
        state.dropPeriod = state.dropPeriod.map((dropPeriod) => (dropPeriod.id === action.payload.id ? action.payload : dropPeriod));
        message.success("Успешно изменено");
        state.status = "idle";
      },
      [`${rejected}`]: (state: DropPeriodState, action: PayloadAction<AxiosError>) => {
        const error = serializeError(action.payload);
        state.status = "failed";
        message.error("Произошла ошибка !");
        state.error = error;
      },
    };
  };

  const removeDropPeriod = () => {
    const { pending, fulfilled, rejected } = deleteDropPeriodAsync;
    return {
      [`${pending}`]: (state: DropPeriodState) => {
        state.status = "loading";
      },
      [`${fulfilled}`]: (state: DropPeriodState, action: PayloadAction<DropPeriod>) => {
        state.dropPeriod.splice(state.dropPeriod.indexOf(
          state.dropPeriod.find((ws: DropPeriod) => ws.id === action.payload.id) || action.payload
        ), 1);
        message.success("Успешно удален");
        state.status = "idle";
      },
      [`${rejected}`]: (state: DropPeriodState, action: PayloadAction<AxiosError>) => {
        const error = serializeError(action.payload);
        state.status = "failed";
        message.error("Произошла ошибка !");
        state.error = error;
      },
    };
  };

  return { ...fetchDropPeriodByOrganization(), ...createDropPeriod(), ...updateDropPeriod(), ...removeDropPeriod() };
};

export const dropPeriodSlice = createSlice({
  name: "dropPeriod",
  initialState,
  reducers: {},
  extraReducers: createExtraReducers(),
});

export const selectDropPeriod = (state: RootState) => state.dropPeriod.dropPeriod;

export default dropPeriodSlice.reducer;
